import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ClipLoader } from "react-spinners";
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import AddRoadIcon from '@mui/icons-material/AddRoad';
import SaveIcon from '@mui/icons-material/Save';

function Tours() {
  const [dayCount, setDayCount] = useState(0);
  const [inputs, setInputs] = useState([]);
  const [showInputs, setShowInputs] = useState(false);
  const [nextId, setNextId] = useState(null);
  const [tourDescription, setTourDescription] = useState('');
  const [tourRate, setTourRate] = useState('');
  const [tourTitle, setTourTitle] = useState('');

  // NEW: Route overview fields
  const [routeImage, setRouteImage] = useState(null);
  const [routeDescription, setRouteDescription] = useState('');
  // Key to force-reset the file input after submit
  const [routeImageKey, setRouteImageKey] = useState(0);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getnextId();
  }, []);

  // (Slightly cleaned) async/await version
  const getnextId = async () => {
    try {
      const res = await axios.get('https://genzserver.mylksystems.com/Tours/getnextId');
      if (res?.data?.nextId != null) {
        setNextId(res.data.nextId);
        console.log('Next Tour ID:', res.data.nextId);
      }
    } catch (error) {
      console.error('Error fetching next ID:', error);
    }
  };

  const handleDayChange = (e) => {
    const n = parseInt(e.target.value, 10);
    setDayCount(Number.isNaN(n) ? 0 : n);
  };

  const handleSubmitDays = () => {
    const newInputs = Array.from({ length: dayCount }, () => ({
      photo: null,
      description: '',
    }));
    setInputs(newInputs);
    setShowInputs(true);
  };

  const handlePhotoChange = (index, file) => {
    const updated = [...inputs];
    updated[index].photo = file;
    setInputs(updated);
  };

  const handleDescriptionChange = (index, desc) => {
    const updated = [...inputs];
    updated[index].description = desc;
    setInputs(updated);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append('tourID', nextId);
    formData.append('title', tourTitle);
    formData.append('description', tourDescription);
    formData.append('rate', tourRate);
    formData.append('days', dayCount);

    // NEW: route_image + route_description
    formData.append('route_image', routeImage);             // EXACT KEY
    formData.append('route_description', routeDescription); // EXACT KEY

    inputs.forEach((item, index) => {
      formData.append(`day_${index + 1}_photo`, item.photo);
      formData.append(`day_${index + 1}_description`, item.description);
    });

    try {
      await axios.post(
        'https://genzserver.mylksystems.com/Tours/createTour',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      // Refresh next ID after successful submission
      await getnextId();

      // Reset UI
      setDayCount(0);
      setInputs([]);
      setShowInputs(false);
      setTourDescription('');
      setTourRate('');
      setTourTitle('');

      // NEW: Reset route overview fields (including file input)
      setRouteImage(null);
      setRouteDescription('');
      setRouteImageKey((k) => k + 1);
    } catch (error) {
      console.error('Error submitting tour:', error);
      alert('Submission failed. Check console for details.');
    } finally {
      // Ensure spinner stops even on error
      setLoading(false);
    }
  };

  return (
    <div className="page-shell page-shell-narrow">
      <header className="page-header">
        <div>
          <span className="eyebrow">Tour builder</span>
          <h1 className="page-title">Create a new tour</h1>
          <p className="page-copy">
            Add the cover story, route overview, and each day of the travel itinerary.
          </p>
        </div>
      </header>

      <div className="admin-card form-card">
        <form onSubmit={handleFormSubmit} className="form-grid">
          <div className="field">
            <label>Tour Title</label>
            <textarea
              value={tourTitle}
              onChange={(e) => setTourTitle(e.target.value)}
              rows={3}
              placeholder="Enter a short title for the tour"
              required
            />
          </div>

          <div className="field">
            <label>Tour Description</label>
            <textarea
              value={tourDescription}
              onChange={(e) => setTourDescription(e.target.value)}
              rows={3}
              placeholder="Enter a short description for the tour"
              required
            />
          </div>

          <div className="field">
            <label>Tour Rate</label>
            <input
              value={tourRate}
              onChange={(e) => setTourRate(e.target.value)}
              placeholder="Enter Tour Rate in LKR"
              type="number"
              min="0"
              required
            />
          </div>

          <section className="section-card">
            <h2 className="section-title">Route Overview</h2>
            <div className="form-grid">
              <div className="field">
                <label>Route Photo</label>
                <input
                  key={routeImageKey}
                  type="file"
                  accept="image/*"
                  onChange={(e) => setRouteImage(e.target.files?.[0] || null)}
                  required
                />
              </div>

              <div className="field">
                <label>Route Description</label>
                <textarea
                  value={routeDescription}
                  onChange={(e) => setRouteDescription(e.target.value)}
                  rows={4}
                  placeholder="Describe the overall route: start/end points, attractions, and highlights"
                  required
                />
              </div>
            </div>
          </section>

          <div className="form-row-inline">
            <div className="field" style={{ flex: 1 }}>
              <label>Number of days</label>
              <input
                type="number"
                min="1"
                max="30"
                value={dayCount}
                onChange={handleDayChange}
                placeholder="Enter number of days"
              />
            </div>
            <button type="button" className="admin-button secondary" onClick={handleSubmitDays}>
              <AddRoadIcon fontSize="small" />
              Submit Days
            </button>
          </div>

          {showInputs &&
            inputs.map((input, index) => (
              <section className="section-card" key={index}>
                <h2 className="section-title">Day {index + 1}</h2>
                <div className="form-grid">
                  <div className="field">
                    <label>Photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoChange(index, e.target.files[0])}
                      required
                    />
                  </div>

                  <div className="field">
                    <label>Description</label>
                    <textarea
                      value={input.description}
                      onChange={(e) => handleDescriptionChange(index, e.target.value)}
                      rows={4}
                      placeholder={`Enter description for Day ${index + 1}`}
                      required
                    />
                  </div>
                </div>
              </section>
            ))}

          {showInputs && (
            <div>
              <button type="submit" className="admin-button" disabled={loading}>
                {loading ? <ClipLoader color="#ffffff" size={20} /> : <SaveIcon fontSize="small" />}
                {loading ? 'Submitting...' : 'Submit Tour'}
              </button>
            </div>
          )}

          {!showInputs && (
            <div className="form-note">
              <AddPhotoAlternateIcon fontSize="small" /> Enter the number of days to open itinerary photo and description fields.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default Tours;
